import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/base/JsonLd';

export default function About() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${siteUrl}/about#webpage`,
    url: `${siteUrl}/about`,
    name: 'About Celebrity Home Health Las Vegas | Our Story & Mission',
    description: 'Learn about Celebrity Home Health — founded in 2023, Medicare certified and ACHC accredited, led by Dr. Osmel Villarreal, DBA. Serving Las Vegas families with compassionate home health care.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: {
      '@type': 'HomeHealthService',
      '@id': `${siteUrl}/#organization`,
      name: 'Celebrity Home Health',
      foundingDate: '2023',
      hasCredential: ['Medicare Certified 2025', 'ACHC Accredited 2025'],
      employee: {
        '@type': 'Person',
        name: 'Dr. Osmel Villarreal, DBA, MBA, MS',
        jobTitle: 'Administrator & Founder',
        description: 'ACHC Certified Consultant with expertise in Medicare Conditions of Participation and home health regulatory compliance.',
      },
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'About Celebrity Home Health Las Vegas | Our Story & Mission';
  }, []);

  const values = [
    { icon: 'ri-heart-pulse-line', title: 'Compassionate Care', desc: 'We treat every patient with dignity, respect, and genuine compassion, understanding that healing extends beyond physical health.' },
    { icon: 'ri-shield-check-line', title: 'ACHC Accredited', desc: 'Accredited by the Accreditation Commission for Health Care — a nationally recognized symbol of quality and excellence.' },
    { icon: 'ri-user-heart-line', title: 'Patient-Centered', desc: 'Every care plan is personalized to meet individual needs, preferences, and goals, ensuring the best possible outcomes.' },
    { icon: 'ri-team-line', title: 'Professional Team', desc: 'Our licensed healthcare professionals bring expertise, experience, and dedication to every patient interaction.' },
    { icon: 'ri-home-heart-line', title: 'Home Comfort', desc: 'We believe healing happens best at home, surrounded by loved ones in a familiar, comfortable environment.' },
    { icon: 'ri-time-line', title: '24/7 Support', desc: 'Round-the-clock availability ensures our patients and families always have access to care when they need it most.' },
  ];

  const timeline = [
    { year: '2023', title: 'Founded', desc: 'Celebrity Home Health established in Las Vegas with a mission to deliver compassionate, high-quality home health care to the community.' },
    { year: '2024', title: 'Rapid Growth', desc: 'Expanded our team of skilled nurses, therapists, and care professionals to meet growing patient demand across the Las Vegas Valley.' },
    { year: '2025', title: 'Medicare Certified', desc: 'Achieved Medicare certification, a major milestone that allows us to serve a broader range of patients and families.' },
    { year: '2025', title: 'ACHC Accredited', desc: 'Earned accreditation from the Accreditation Commission for Health Care (ACHC), demonstrating our commitment to the highest standards of care.' },
  ];

  const team = [
    {
      img: 'https://public.readdy.ai/ai/img_res/a6567f8b167d24e405aea2c9b0a08b39.jpg',
      name: 'Dr. Osmel Villarreal, DBA',
      role: 'Administrator',
      desc: 'A highly qualified healthcare executive and ACHC-certified consultant, Dr. Villarreal leads Celebrity Home Health with deep expertise in Medicare Conditions of Participation, regulatory compliance, and accreditation standards — ensuring care delivered above industry benchmarks.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={aboutJsonLd as Record<string, unknown>} id="json-ld-about" />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=warm-caring-home-health-nurse-smiling-with-elderly-patient-sitting-together-at-bright-window-genuine-human-connection-soft-natural-daylight-clean-modern-home-interior-professional-compassionate-healthcare&width=1440&height=600&seq=about-hero-2026&orientation=landscape"
            alt="About Celebrity Home Health"
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900/95"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6">
              <i className="ri-information-line"></i> About Us
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Trusted Home Health<br />
              Care Since <span className="text-amber-400">2023</span>
            </h1>
            <p className="text-slate-300 text-xl leading-relaxed">
              Celebrity Home Health was founded with a simple but powerful mission: to provide high-quality, compassionate home health care that enables people to recover and thrive in the comfort of their own homes.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="bg-amber-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <div className="grid grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-1">2023</div>
              <div className="text-amber-100 text-sm">Founded</div>
            </div>
            <div className="border-x border-amber-500">
              <div className="text-4xl font-bold mb-1">Medicare</div>
              <div className="text-amber-100 text-sm">Certified 2025</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-1">ACHC</div>
              <div className="text-amber-100 text-sm">Accredited</div>
            </div>
          </div>
        </div>
      </div>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative w-full h-[500px] rounded-3xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/2271c221-4028-4954-ba67-e2b66eec11ce/746a6434-68f7-47e8-8af0-1ba0e8d7467c_Med-Care-Celebrity-and-Advantixx-Office.png?v=8237b076c97d520d9460ffea8f33b8ca"
                alt="Celebrity Home Health Office - Las Vegas, NV"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div>
              <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
                Our Story
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Committed to Excellence<br />in Home Health Care
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Founded in 2023, Celebrity Home Health was established with a clear vision: to provide high-quality, compassionate home health care services that enable individuals to recover and thrive in the comfort of their own homes.
                </p>
                <p>
                  In 2025, we achieved Medicare certification — a significant milestone that expanded our ability to serve patients and families across the greater Las Vegas Valley. That same year, we earned accreditation from the Accreditation Commission for Health Care (ACHC), one of the most respected accrediting bodies in the industry.
                </p>
                <p>
                  Our ACHC accreditation and Medicare certification reflect our unwavering commitment to quality care, patient safety, and clinical excellence. Every member of our team shares a passion for making a real difference in the lives of those we serve.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Request Care <i className="ri-arrow-right-line"></i>
                </a>
                <a href="/services" className="inline-flex items-center gap-2 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-slate-900 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Our Services <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              Our Foundation
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Mission & Values</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Guided by our core principles, we strive to deliver exceptional care every single day.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-5">
                  <i className={`${v.icon} text-amber-400 text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{v.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              Meet The Administrator
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Administrator</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Celebrity Home Health is led by a highly qualified healthcare executive with deep expertise in clinical compliance and accreditation.</p>
          </div>

          <div className="flex justify-center">
            {team.map((member, i) => (
              <div key={i} className="group rounded-3xl overflow-hidden border border-stone-200 hover:border-amber-300 transition-all duration-300 hover:-translate-y-1 max-w-2xl w-full">
                <div className="grid sm:grid-cols-2">
                  <div className="relative w-full h-80 sm:h-auto overflow-hidden">
                    <img
                      src={member.img}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent sm:bg-gradient-to-r sm:from-transparent sm:to-transparent"></div>
                  </div>
                  <div className="p-10 flex flex-col justify-center bg-white">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 rounded-full mb-5 w-fit">
                      <i className="ri-award-fill text-amber-600 text-sm"></i>
                      <span className="text-amber-700 text-xs font-semibold uppercase tracking-wider">ACHC Certified Consultant</span>
                    </div>
                    <h3 className="font-bold text-slate-900 text-2xl mb-1">{member.name}</h3>
                    <p className="text-amber-600 font-semibold text-base mb-5">{member.role}</p>
                    <p className="text-slate-500 text-sm leading-relaxed mb-6">{member.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {['DBA', 'MBA', 'MS'].map((deg) => (
                        <span key={deg} className="px-3 py-1 bg-slate-900 text-amber-400 rounded-lg text-xs font-bold">{deg}</span>
                      ))}
                      <span className="px-3 py-1 bg-amber-600 text-white rounded-lg text-xs font-bold">ACHC</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership & Commitment Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
                Leadership &amp; Excellence
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Our Leadership &amp; Commitment<br />to Excellence
              </h2>
              <p className="text-slate-600 leading-relaxed mb-5">
                At Celebrity Home Health, Inc., clinical excellence and regulatory compliance are not just priorities — they are the <strong>foundation of our organization</strong>.
              </p>
              <p className="text-slate-600 leading-relaxed mb-5">
                Under the leadership of <strong>Dr. Osmel Villarreal, DBA</strong>, our agency is guided by a highly qualified healthcare executive who brings both operational expertise and advanced regulatory knowledge to the field of home health. Dr. Villarreal is not only the Administrator of the agency, but also a <strong>certified consultant trained through ACHC</strong> (Accreditation Commission for Health Care), with specialized education in home health standards and compliance.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                This unique combination of leadership and accreditation-level expertise ensures that Celebrity Home Health, Inc. operates <strong>above industry standards</strong>, maintaining strict adherence to Medicare Conditions of Participation, state regulations, and best practices in patient care.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-shield-check-line', text: 'Uncompromising compliance and survey readiness' },
                  { icon: 'ri-stethoscope-line', text: 'Clinically driven care models aligned with national standards' },
                  { icon: 'ri-line-chart-line', text: 'Continuous quality improvement and performance excellence' },
                  { icon: 'ri-award-line', text: 'Leadership oversight rooted in accreditation-level knowledge' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-amber-700 text-base`}></i>
                    </div>
                    <p className="text-slate-700 text-sm font-medium pt-2">{item.text}</p>
                  </div>
                ))}
              </div>

              <blockquote className="border-l-4 border-amber-500 pl-5 py-1">
                <p className="text-slate-600 italic leading-relaxed text-sm">
                  "At Celebrity Home Health, we don't just provide care — we deliver <strong>confidence, integrity, and excellence</strong> in every visit, setting a higher standard for home health services in our community."
                </p>
                <footer className="mt-3 text-amber-700 font-semibold text-sm">— Dr. Osmel Villarreal, DBA, MBA, MS · Founder &amp; Administrator</footer>
              </blockquote>
            </div>

            <div className="space-y-5">
              <div className="relative w-full h-[360px] rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=confident-professional-hispanic-male-healthcare-executive-doctor-in-business-suit-standing-in-modern-bright-medical-office-authoritative-warm-smile-leadership-portrait-clean-white-background-natural-light-accomplished-administrator-DBA-degree-healthcare-compliance&width=700&height=360&seq=villarreal-leadership-2026&orientation=landscape"
                  alt="Dr. Osmel Villarreal - Founder & Administrator"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4">
                    <p className="text-white font-bold text-sm">Dr. Osmel Villarreal, DBA, MBA, MS</p>
                    <p className="text-amber-300 text-xs">Founder &amp; Administrator · ACHC Certified Consultant</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: 'ri-award-fill', label: 'ACHC Certified', sub: 'Consultant' },
                  { icon: 'ri-government-line', label: 'Medicare CoP', sub: 'Compliance Expert' },
                  { icon: 'ri-shield-star-line', label: 'Survey Ready', sub: 'Always' },
                ].map((badge, i) => (
                  <div key={i} className="bg-slate-900 rounded-2xl p-5 text-center">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <i className={`${badge.icon} text-amber-400 text-xl`}></i>
                    </div>
                    <p className="text-white font-bold text-xs leading-snug">{badge.label}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{badge.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-4">
              Our Journey
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Milestones of Excellence</h2>
            <p className="text-slate-400 text-lg">A look at the key moments that shaped Celebrity Home Health.</p>
          </div>

          <div className="relative">
            <div className="absolute left-[88px] top-0 bottom-0 w-px bg-slate-700 hidden sm:block"></div>
            <div className="space-y-8">
              {timeline.map((item, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-20 text-right hidden sm:block">
                    <span className={`text-lg font-bold ${i % 2 === 0 ? 'text-amber-400' : 'text-slate-400'}`}>{item.year}</span>
                  </div>
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-1 hidden sm:block ${i % 2 === 0 ? 'bg-amber-500 border-amber-400' : 'bg-slate-600 border-slate-500'}`}></div>
                  <div className="flex-1 bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <div className={`text-sm font-bold mb-0.5 sm:hidden ${i % 2 === 0 ? 'text-amber-400' : 'text-slate-400'}`}>{item.year}</div>
                    <h3 className="text-white font-bold text-base mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">Ready to Join Our Story?</h2>
          <p className="text-amber-100 text-lg mb-8">Whether you're seeking care or looking to join our team, we'd love to connect with you.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white hover:bg-stone-50 text-amber-700 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              Request Care
            </a>
            <a href="/careers" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-700 hover:bg-amber-800 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              View Career Opportunities
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
